import { useCloseDossier } from "@/entities/dossier";
import { DossierDetails, useGetDossierDetailsById } from "@/entities/dossier-details";
import { DossierOverzicht, UitgezetteDienst, useGetDossierOverzicht } from "@/entities/dossier-overzicht";
import { Evaluatie, useGetDossierEvaluatieById, usePutEvaluatie } from "@/entities/evaluatie";
import { useGetHerinneringen } from "@/entities/herinnering/hooks.ts";
import { getReikwijdteLabel } from "@/features/dossier-details";
import { useDossierPinStore } from "@/features/dossier-management";
import { buildEvaluatiePayload } from "@/features/evaluatie/build-evaluatie-payload";
import {
    Button,
    Card,
    Dialog,
    DialogBody,
    DialogContainer,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    ErrorDisplay,
    MessageBar,
    TextareaField,
} from "@/shared/components";
import { Dispatch, SetStateAction, useState } from "react";
import { useNavigate } from "react-router";
import { useCloseDossierStore } from "./close-dossier-store";
import { OpenstaandeOnderdelen } from "./openstaande-onderdelen";
import { getOpenstaandeOnderdelen } from "./openstaande-onderdelen.utils";

type CloseDossierDialogData = {
    dossierDetails?: DossierDetails | null;
    dossier?: DossierOverzicht;
    evaluatie: Evaluatie | null;
    isPending: boolean;
    error: Error | null;
};

function useCloseDossierDialogData(dossierNummer: string): CloseDossierDialogData {
    const dossierDetailsQuery = useGetDossierDetailsById(dossierNummer);
    const dossierOverzichtQuery = useGetDossierOverzicht("Open", (items) => items.find((item) => String(item.dossierId) === dossierNummer));
    const evaluatieQuery = useGetDossierEvaluatieById(dossierNummer);

    const isPending = dossierDetailsQuery.isPending || dossierOverzichtQuery.isPending || evaluatieQuery.isPending;

    const error = dossierDetailsQuery.error || dossierOverzichtQuery.error || evaluatieQuery.error || null;

    return {
        dossierDetails: dossierDetailsQuery.data,
        dossier: dossierOverzichtQuery.data,
        evaluatie: evaluatieQuery.data ?? null,
        isPending,
        error,
    };
}

type CloseDossierDialogProps = {
    dossierNummer: string;
    open: boolean;
    onOpenChange: Dispatch<SetStateAction<boolean>>;
};

export function CloseDossierDialog({ dossierNummer, open, onOpenChange }: CloseDossierDialogProps) {
    const { dossier, dossierDetails, evaluatie, isPending, error } = useCloseDossierDialogData(dossierNummer);

    if (error) return <ErrorDisplay error={error} />;

    if (isPending || !dossierDetails || !dossier) {
        return null;
    }

    return (
        <CloseDossierDialogContent
            dossierNummer={dossierNummer}
            uitgezetteDiensten={dossier.uitgezetteDiensten}
            dossierDetails={dossierDetails}
            evaluatie={evaluatie}
            open={open}
            onOpenChange={onOpenChange}
        />
    );
}

type CloseDossierDialogContentProps = {
    dossierNummer: string;
    uitgezetteDiensten: UitgezetteDienst[];
    dossierDetails: DossierDetails;
    evaluatie: Evaluatie | null;
    open: boolean;
    onOpenChange: Dispatch<SetStateAction<boolean>>;
};

function CloseDossierDialogContent({
    dossierNummer,
    uitgezetteDiensten,
    dossierDetails,
    evaluatie,
    open,
    onOpenChange,
}: CloseDossierDialogContentProps) {
    const [toelichting, setToelichting] = useState("");
    const closeDossier = useCloseDossier();
    const putEvaluatie = usePutEvaluatie();
    const navigate = useNavigate();
    const { data: heeftHerinneringen } = useGetHerinneringen(dossierNummer, { select: (herinneringen) => herinneringen.length > 0 });
    const setCurrentlyClosingDossierNummer = useCloseDossierStore((state) => state.setCurrentlyClosingDossierNummer);
    const unpin = useDossierPinStore((state) => state.unpin);

    const { omschrijving, omschrijvingExtraContext } = dossierDetails;

    const heeftKhrStatus = evaluatie?.heeftKhrStatus ?? false;

    const openstaandeOnderdelen = getOpenstaandeOnderdelen(uitgezetteDiensten, {
        heeftKhrStatus,
        icDocLink: evaluatie?.icDocLink,
    });
    const heeftOpenstaandeOnderdelen = openstaandeOnderdelen.some((item) => item.status === "openstaand");

    const handleCloseDossier = async () => {
        setCurrentlyClosingDossierNummer(dossierNummer);
        await closeDossier.mutateAsync({ dossierNummer, toelichting: toelichting !== "" ? toelichting : undefined });
        unpin(Number(dossierNummer));
        onOpenChange(false);
        void navigate("/");
    };

    const handleToggleHeeftKHRStatus = (heeftKhrStatus: boolean) => {
        const evaluatiePayload = buildEvaluatiePayload(evaluatie, { heeftKhrStatus });
        putEvaluatie.mutate({ dossierId: dossierNummer, ...evaluatiePayload });
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContainer className="md:min-w-[736px]">
                <DialogHeader>
                    <DialogTitle>Dossier beëindigen</DialogTitle>
                </DialogHeader>
                {putEvaluatie.error && <ErrorDisplay error={putEvaluatie.error} />}
                {closeDossier.error && <ErrorDisplay error={closeDossier.error} />}
                <DialogBody className="grid gap-4">
                    <Card className="border-l-content-info text-content-emphasis relative grid gap-1 border-l-4 p-3 pl-4 font-medium">
                        <p className="text-s leading-m uppercase" data-testid="reikwijdte">
                            {getReikwijdteLabel(dossierDetails.reikwijdte, dossierDetails.stations, dossierDetails.trajecten)}
                        </p>
                        <h3 className="text-xxxl" data-testid="omschrijving">
                            {omschrijving}
                        </h3>
                        <p className="leading-m text-xl" data-testid="omschrijving-extra-context">
                            {omschrijvingExtraContext}
                        </p>
                    </Card>
                    <div className="grid gap-2">
                        <h4>Controleer openstaande onderdelen</h4>
                        {heeftOpenstaandeOnderdelen && (
                            <MessageBar className="rounded-m px-3">
                                Ga naar het desbetreffende onderdeel om het af te sluiten. Sommige onderdelen vereisen eerst goedkeuring van
                                een andere partij.
                            </MessageBar>
                        )}
                        {heeftHerinneringen && (
                            <MessageBar variant="warning" className="rounded-m px-3">
                                Er staan nog openstaande herinneringen in de tijdlijn. Deze verdwijnen als je het dossier beëindigt.
                            </MessageBar>
                        )}
                        <Card>
                            <OpenstaandeOnderdelen
                                openstaandeOnderdelen={openstaandeOnderdelen}
                                heeftKHRStatus={heeftKhrStatus}
                                onHeeftKHRStatusChange={handleToggleHeeftKHRStatus}
                            />
                        </Card>
                    </div>
                    <TextareaField
                        rows={4}
                        label="Toelichting beëindiging dossier"
                        value={toelichting}
                        onValueChange={setToelichting}
                        helptext="Toelichting wordt in Wissel getoond als reden van beëindiging dossier."
                    />
                    {heeftOpenstaandeOnderdelen && (
                        <p className="text-content-subtle ml-auto text-xs">
                            Sluit eerst alle openstaande onderdelen af voordat u het dossier kunt beëindigen.
                        </p>
                    )}
                </DialogBody>
                <DialogFooter>
                    <Button variant="tertiary" onClick={() => onOpenChange(false)}>
                        Annuleren
                    </Button>
                    <Button
                        variant="primary"
                        disabled={heeftOpenstaandeOnderdelen || putEvaluatie.isPending || closeDossier.isPending}
                        onClick={handleCloseDossier}
                    >
                        Beëindig dossier
                    </Button>
                </DialogFooter>
            </DialogContainer>
        </Dialog>
    );
}
